/**
 * normal_from_albedo.ts — runtime tangent-space normal maps derived from the
 * biome ALBEDO textures (Sobel on luminance), so per-biome surface relief needs
 * ZERO new assets: the six ground albedos we already ship become six normal
 * maps at load time. Luminance-as-height is an approximation, but on
 * rocky/mossy/sandy ground textures it reads exactly like authored detail
 * normals at terrain viewing distances — and it stays fully self-hosted.
 */

import * as THREE from 'three/webgpu';

/** Build a tangent-space normal map from an albedo texture's luminance. */
export function normalFromAlbedo(albedo: THREE.Texture, strength = 2.6, size = 512): THREE.Texture | null {
  const img = albedo.image as (HTMLImageElement | ImageBitmap | undefined);
  if (!img || !(img.width > 0) || !(img.height > 0)) return null;

  const w = Math.min(size, img.width);
  const h = Math.min(size, img.height);
  const src = document.createElement('canvas');
  src.width = w;
  src.height = h;
  const sg = src.getContext('2d', { willReadFrequently: true });
  if (!sg) return null;
  sg.drawImage(img as CanvasImageSource, 0, 0, w, h);
  const data = sg.getImageData(0, 0, w, h).data;

  // Luminance "heightfield" (wrapping — the albedos tile).
  const lum = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) {
    lum[i] = (0.2126 * data[i * 4] + 0.7152 * data[i * 4 + 1] + 0.0722 * data[i * 4 + 2]) / 255;
  }
  const at = (x: number, y: number): number => lum[((y + h) % h) * w + ((x + w) % w)];

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const og = out.getContext('2d')!;
  const px = og.createImageData(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      // Sobel gradients (wrapped)
      const gx =
        (at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1)) -
        (at(x - 1, y - 1) + 2 * at(x - 1, y) + at(x - 1, y + 1));
      const gy =
        (at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1)) -
        (at(x - 1, y - 1) + 2 * at(x, y - 1) + at(x + 1, y - 1));
      // Tangent-space normal (Y-up convention matches three's normal maps)
      let nx = -gx * strength;
      let ny = -gy * strength;
      let nz = 1;
      const inv = 1 / Math.sqrt(nx * nx + ny * ny + nz * nz);
      nx *= inv; ny *= inv; nz *= inv;
      const o = (y * w + x) * 4;
      px.data[o] = Math.round((nx * 0.5 + 0.5) * 255);
      px.data[o + 1] = Math.round((ny * 0.5 + 0.5) * 255);
      px.data[o + 2] = Math.round((nz * 0.5 + 0.5) * 255);
      px.data[o + 3] = 255;
    }
  }
  og.putImageData(px, 0, 0);

  const tex = new THREE.CanvasTexture(out);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.copy(albedo.repeat);
  tex.colorSpace = THREE.NoColorSpace; // normals are DATA, never sRGB
  tex.anisotropy = albedo.anisotropy;
  return tex;
}
